import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigationType,
  Location,
} from "react-router-dom";
import IdleScreen from "../pages/idle";
import QRScanScreen from "../pages/qr-scan";
import MainScreen from "../pages/main";
import MagicScreen from "../pages/magic";
import ColorSelectionScreen from "../pages/color-selection";
import LanternSelectedScreen from "../pages/selected-lantern";
import JoiningTodayScreen from "../pages/joining-today";
import ChooseAvatarScreen from "../pages/choose-avatar";
import ChooseFriendAvatarScreen from "../pages/choose-firiend-avatar";
import KioskSessionManager from "../compoment/KioskSessionManager";
import GlobalPinHandler from "../compoment/GlobalPinHandler";
import PWAInstallPrompt from "../compoment/PWAInstallPrompt";

/** Keep in sync with the stack animation duration in App.scss. */
const ROUTE_TRANSITION_MS = 462;

/**
 * Transitions normally finish on `animationend`. This is only a fallback for
 * when no animation runs (e.g. prefers-reduced-motion). It has headroom because
 * heavy pages (QR camera) can block the main thread while mounting, which
 * delays the animation's start but not a timer's.
 */
const ROUTE_TRANSITION_FALLBACK_MS = ROUTE_TRANSITION_MS + 1000;

const STACK_ANIMATIONS = new Set([
  "stackEnterFromRight",
  "stackExitToRight",
  "stackDimUnder",
  "stackUndimUnder",
]);

/** Position of each screen in the flow; lower depth = navigating back. */
const ROUTE_DEPTH: Record<string, number> = {
  "/": 0,
  "/qr-scan": 1,
  "/main": 2,
  "/color-selection": 3,
  "/selected-lantern": 4,
  "/today-joining": 5,
  "/choose-avatar": 6,
  "/choose-friend-avatar": 7,
  "/magic": 8,
};

const routeDepth = (pathname: string) => ROUTE_DEPTH[pathname] ?? 0;

const isBackNavigation = (from: string, to: string, navigationType: string) =>
  navigationType === "POP" || routeDepth(to) < routeDepth(from);

const renderAppRoutes = (location: Location) => (
  <Routes location={location}>
    {/* ================= COMMON ROUTE ================= */}
    <Route path="/" element={<IdleScreen />} />
    <Route path="/qr-scan" element={<QRScanScreen />} />
    <Route path="/main" element={<MainScreen />} />
    <Route path="/color-selection" element={<ColorSelectionScreen />} />
    <Route path="/selected-lantern" element={<LanternSelectedScreen />} />
    <Route path="/today-joining" element={<JoiningTodayScreen />} />
    <Route path="/choose-avatar" element={<ChooseAvatarScreen />} />
    <Route path="/choose-friend-avatar" element={<ChooseFriendAvatarScreen />} />
    {/* <Route path="/take-10s-video" element={<TakeVideo10SecScreen />} />
    <Route path="/qr-code-for-image-upload" element={<QRCodeForImageUploadScreen />} /> */}
    <Route path="/magic" element={<MagicScreen />} />
  </Routes>
);

/**
 * Stacked page transition (same motion as social-collage): the new page slides
 * in from the right over the old one, which blurs and dims underneath; going
 * back reverses it.
 *
 * Layers are keyed by pathname only, so the outgoing page keeps its existing
 * instance while it animates out instead of remounting (which would re-run its
 * effects — camera, sockets, timers).
 */
const AnimatedRoutes = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const displayedRef = useRef<Location>(location);
  const [displayed, setDisplayed] = useState<Location>(location);
  const [outgoing, setOutgoing] = useState<Location | null>(null);
  const [direction, setDirection] = useState<"forward" | "back">("forward");

  useLayoutEffect(() => {
    if (location.pathname === displayedRef.current.pathname) {
      displayedRef.current = location;
      setDisplayed(location);
      return;
    }

    const goingBack = isBackNavigation(
      displayedRef.current.pathname,
      location.pathname,
      navigationType
    );
    setDirection(goingBack ? "back" : "forward");
    setOutgoing(displayedRef.current);
    displayedRef.current = location;
    setDisplayed(location);
  }, [location, navigationType]);

  useEffect(() => {
    if (!outgoing) return undefined;
    const timer = window.setTimeout(
      () => setOutgoing(null),
      ROUTE_TRANSITION_FALLBACK_MS
    );
    return () => window.clearTimeout(timer);
  }, [outgoing, displayed.pathname]);

  const handleAnimationEnd = (event: React.AnimationEvent<HTMLDivElement>) => {
    // Ignore animations bubbling up from inside the pages.
    if (event.target !== event.currentTarget) return;
    if (!STACK_ANIMATIONS.has(event.animationName)) return;
    setOutgoing(null);
  };

  const layers = [
    ...(outgoing
      ? [{ location: outgoing, className: `routeTransition--exit-${direction}`, exiting: true }]
      : []),
    {
      location: displayed,
      className: outgoing ? `routeTransition--enter-${direction}` : "",
      exiting: false,
    },
  ];

  return (
    <div className="routeTransitionHost">
      {layers.map((layer) => (
        <div
          key={layer.location.pathname}
          className={`routeTransition ${layer.className}`.trim()}
          aria-hidden={layer.exiting || undefined}
          onAnimationEnd={handleAnimationEnd}
        >
          {renderAppRoutes(layer.location)}
        </div>
      ))}
    </div>
  );
};

const AppRoutes = () => {
  return (
    <Router>
      <KioskSessionManager />
      <GlobalPinHandler />
      <PWAInstallPrompt />
      <AnimatedRoutes />
    </Router>
  );
};

export default AppRoutes;
